import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Fab from '@mui/material/Fab';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import Empty from '@maya/common/Empty';
import Page from '@maya/common/Page';
import Loading from '@maya/common/loading/Loading';
import TableCell from '@maya/common/table/TableCell';
import { PAGE_HEIGHT } from '@maya/constants';
import useDebounce from '@maya/hooks/useDebounce';
import useTranslate from '@maya/hooks/useTranslate';
import { useAppDispatch, useAppSelector } from '@maya/store/hooks';
import { saveUser, selectTotalUsers, selectUserLoading } from '@maya/store/slices/user';
import { isNotEmpty } from '@maya/util/string.util';
import useUsers from './hooks/useUsers';

import type { EmployeeDTO } from '@maya/interface';
import type { FC, MouseEvent } from 'react';

const UserList: FC = () => {
  const t = useTranslate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);

  const users = useUsers(debouncedSearch);
  const totalUsers = useAppSelector(selectTotalUsers);
  const usersLoading = useAppSelector(selectUserLoading);

  const dispatch = useAppDispatch();
  const toggleUserActive = useCallback(
    (user: EmployeeDTO) => (event: MouseEvent) => {
      event.stopPropagation();

      dispatch(
        saveUser(
          {
            ...user,
            active: !user.active
          },
          t
        )
      );
    },
    [dispatch, t]
  );

  return (
    <Page title={t('user.list.header')}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: `calc(100vh - ${PAGE_HEIGHT}px)`
        }}
      >
        <Card sx={{ flexGrow: 1 }}>
          <CardContent
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              paddingBottom: '12px!important'
            }}
          >
            <TextField
              id="filled-basic"
              label={t('app.search')}
              variant="filled"
              role="search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            {usersLoading ? (
              <Loading />
            ) : users.length === 0 ? (
              <Empty key="empty" image="list">
                {t(isNotEmpty(debouncedSearch) ? 'user.list.emptySearch' : 'user.list.empty')}
              </Empty>
            ) : (
              <>
                <TableContainer sx={{ flexGrow: 1 }}>
                  <Table aria-label="user list" stickyHeader component="div">
                    <TableHead component="div">
                      <TableRow component="div">
                        <TableCell>{t('user.list.column.name')}</TableCell>
                        <TableCell>{t('user.list.column.phone')}</TableCell>
                        <TableCell>{t('user.list.column.active')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody component="div">
                      {users.map((user) => (
                        <TableRow
                          component={Link}
                          key={user.id}
                          data-testid={`user-${user.fullName}`}
                          sx={{ textDecoration: 'none', '&:last-child td, &:last-child th': { border: 0 } }}
                          hover
                          to={`/user/details/${user.id}`}
                        >
                          <TableCell>{user.fullName}</TableCell>
                          <TableCell>{user.phone}</TableCell>
                          <TableCell component="div" data-testid="checkbox" sx={{ padding: 0 }}>
                            <Switch checked={user.active} color="secondary" onClick={toggleUserActive(user)} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {t('app.totalRows')} {totalUsers}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
        <Fab
          variant="extended"
          component={Link}
          to="/user/new"
          color="secondary"
          sx={{ alignSelf: 'flex-end', gap: '4px', minHeight: '48px' }}
        >
          <AddIcon />
          {t('user.view.header')}
        </Fab>
      </Box>
    </Page>
  );
};

export default UserList;
